import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, EmptyState, HStack, IconButton, Image, Input, SimpleGrid, Spinner, Text, VStack } from "@chakra-ui/react";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { MdDelete, MdOutlineImage } from "react-icons/md";
import { storage } from "lib/firebase";
import {
  useAddDashboardActividadPhoto,
  useDeleteDashboardActividadPhoto,
} from "features/dashboard/actividades/hooks";

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

function getFileExtension(file) {
  const base = (file?.name ?? "").split(".").pop()?.toLowerCase() ?? "";
  const cleaned = base.replace(/[^a-z0-9]/g, "");
  if (cleaned) return cleaned;
  if (file?.type === "image/jpeg") return "jpg";
  if (file?.type === "image/png") return "png";
  if (file?.type === "image/webp") return "webp";
  return "jpg";
}

function validateImageFile(file) {
  if (!file?.type?.startsWith("image/")) throw new Error("Solo se permiten archivos de imagen.");
  if (file.size > MAX_IMAGE_SIZE_BYTES) throw new Error("El archivo supera el limite de 10MB.");
}

const FotosMenu = ({ actividad }) => {
  const { id: actividadId } = useParams();
  const addOneInputRef = useRef(null);

  const addPhoto = useAddDashboardActividadPhoto();
  const deletePhoto = useDeleteDashboardActividadPhoto();

  const [photosLoading, setPhotosLoading] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState("");
  const [error, setError] = useState("");

  const photos = actividad?.photos ?? [];

  const uploadOne = async (file) => {
    validateImageFile(file);
    const imageId = crypto.randomUUID();
    const ext = getFileExtension(file);
    const storagePath = `actividades/${actividadId}/photos/${imageId}.${ext}`;
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, file, file.type ? { contentType: file.type } : undefined);
    const downloadUrl = await getDownloadURL(storageRef);

    return {
      imageId,
      storagePath,
      downloadUrl,
      contentType: file.type || null,
      sizeBytes: file.size,
    };
  };

  const uploadFiles = async (files) => {
    const list = Array.from(files ?? []);
    if (!list.length || !actividadId) return;

    setError("");
    setPhotosLoading(true);
    try {
      for (const file of list) {
        const payload = await uploadOne(file);
        await addPhoto.mutateAsync({ actividadId, payload });
      }
    } catch (e) {
      setError(String(e?.message || e || "No se pudieron subir las fotos."));
    } finally {
      setPhotosLoading(false);
    }
  };

  const onAddOneChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await uploadFiles([file]);
  };

  const onDeletePhoto = async (photo) => {
    if (!actividadId || !photo?.imageId) return;

    setError("");
    setDeletingPhotoId(photo.imageId);

    try {
      if (photo.storagePath) {
        await deleteObject(ref(storage, photo.storagePath));
      }
    } catch (e) {
      if (String(e?.code || "").toLowerCase() !== "storage/object-not-found") {
        setDeletingPhotoId("");
        setError(String(e?.message || e || "No se pudo borrar la foto del storage."));
        return;
      }
    }

    try {
      await deletePhoto.mutateAsync({ actividadId, imageId: photo.imageId });
    } catch (e) {
      setError(String(e?.message || e || "No se pudo borrar la foto."));
    } finally {
      setDeletingPhotoId("");
    }
  };

  const actions = (
    <HStack gap="4" mt="4">
      <Button
        onClick={() => addOneInputRef.current?.click()}
        disabled={photosLoading || !!deletingPhotoId}
      >
        Agregar Fotos
      </Button>
    </HStack>
  );

  return (
      <VStack align="stretch" gap="4">
      <Input ref={addOneInputRef} type="file" accept="image/*" onChange={onAddOneChange} display="none" />

      {!photos.length ? (
        <EmptyState.Root
          bg="dashboard.bg.surface"
          rounded="2xl"
          boxShadow="xs"
        >
          <EmptyState.Content>
            <EmptyState.Indicator>
              <MdOutlineImage />
            </EmptyState.Indicator>
            <EmptyState.Title>Agrega fotos de tu actividad</EmptyState.Title>
            <EmptyState.Description>
              Empieza agregando fotos de tu actividad para poder mostrar a las familias.
            </EmptyState.Description>
            {actions}
            {photosLoading ? (
              <HStack color="fg.muted" mt="3">
                <Spinner size="sm" />
                <Text>Subiendo fotos...</Text>
              </HStack>
            ) : null}
          </EmptyState.Content>
        </EmptyState.Root>
      ) : (
        <Box
          bg="dashboard.bg.surface"
          rounded="2xl"
          boxShadow="xs"
          p="5"
        >
          <HStack justify="space-between" align="center" mb="3">
            <Text fontWeight="bold">Fotos de la actividad</Text>
            <Button
              size="sm"
              onClick={() => addOneInputRef.current?.click()}
              disabled={photosLoading || !!deletingPhotoId}
            >
              Agregar Foto
            </Button>
          </HStack>

          {photosLoading ? (
            <HStack color="fg.muted" mb="3">
              <Spinner size="sm" />
              <Text>Subiendo fotos...</Text>
            </HStack>
          ) : null}

          <SimpleGrid columns={{ base: 2, md: 3 }} gap="3">
            {photos.map((photo) => (
              <Box key={photo.imageId} position="relative" className="group">
                <Image
                  src={photo.downloadUrl}
                  alt={`Foto ${photo.imageId}`}
                  h="140px"
                  w="full"
                  objectFit="cover"
                  rounded="lg"
                />
                <IconButton
                  size="xs"
                  colorPalette="red"
                  variant="solid"
                  position="absolute"
                  top="2"
                  right="2"
                  opacity={0}
                  pointerEvents="none"
                  _groupHover={{ opacity: 1, pointerEvents: "auto" }}
                  _groupFocusWithin={{ opacity: 1, pointerEvents: "auto" }}
                  transition="opacity 150ms ease"
                  aria-label="Delete photo"
                  onClick={() => onDeletePhoto(photo)}
                  disabled={deletingPhotoId === photo.imageId}
                >
                  {deletingPhotoId === photo.imageId ? <Spinner size="xs" /> : <MdDelete />}
                </IconButton>
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {error ? <Text color="red.500">{error}</Text> : null}
    </VStack>
  );
};

export default FotosMenu;
